// ─────────────────────────────────────────────────────────────────────
// THE DETERMINISTIC DEMO PLAYER BANK.
//
// A seeded 200-player list, stable across loads and across processes: the same
// PRNG seed produces the same ids, names and ratings every time. That stability
// is the point — tournaments and archives reference these ids, so a bank that
// regenerated differently would break every reference to it.
//
// It lives HERE rather than in the web app because the browser is no longer the
// thing that installs it (#272). Seeding a shared, platform-wide player table
// from whichever browser booted first was how one failed read could delete
// every real player; the database is seeded by a script now, and the script and
// the app have to agree about what the bank IS.
//
// Moved verbatim from apps/web/src/engine/playerDb.ts — same generator, same
// seed, same output. The web module re-exports it, so every existing import
// keeps working and the existing tests pin the numbers unchanged.
// ─────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
const FIRST_M = [
  'Erik',
  'Magnus',
  'Henrik',
  'Lars',
  'Tomas',
  'Jonas',
  'Anders',
  'Kristian',
  'Marius',
  'Petter',
  'Sondre',
  'Eirik',
  'Håkon',
  'Sindre',
  'Vegard',
  'Ola',
  'Andreas',
  'Mathias',
  'Daniel',
  'Jakob',
  'Ivar',
  'Bjørn',
  'Trond',
  'Espen',
  'Stian',
];
const FIRST_F = [
  'Ingrid',
  'Sofie',
  'Anna',
  'Maria',
  'Linnea',
  'Camilla',
  'Kristine',
  'Hanne',
  'Marte',
  'Silje',
  'Tuva',
  'Ida',
  'Emma',
  'Nora',
  'Maja',
  'Astrid',
  'Live',
  'Mia',
  'Frida',
  'Sara',
  'Elise',
  'Vilde',
  'Helene',
  'Julie',
  'Thea',
];
const LAST = [
  'Hansen',
  'Olsen',
  'Berg',
  'Solberg',
  'Lindvik',
  'Dahl',
  'Kvam',
  'Aas',
  'Ruud',
  'Foss',
  'Nilsen',
  'Strand',
  'Bakken',
  'Lie',
  'Holm',
  'Ekberg',
  'Moen',
  'Fjell',
  'Vik',
  'Nordby',
  'Haugen',
  'Sand',
  'Eide',
  'Holt',
  'Vold',
];

export function normalizeForEmail(s: string): string {
  return s
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z]/g, '');
}

export function buildPlayerDB(): any[] {
  const players = [];
  let id = 1;
  const seed = ((s) => {
    let x = s;
    return () => {
      x = (x * 9301 + 49297) % 233280;
      return x / 233280;
    };
  })(42);
  const emailCounts = new Map(); // to give unique emails on name collisions

  for (let i = 0; i < 200; i++) {
    const isF = seed() < 0.5;
    const fn = (isF ? FIRST_F : FIRST_M)[Math.floor(seed() * 25)];
    const ln = LAST[Math.floor(seed() * 25)];
    // ELO 500-3000 — uniform distribution for full variation
    // Gives players at all levels, from around 500 (beginners) to 3000 (the elite)
    const elo = Math.round(500 + seed() * 2500);
    const isFavorite = seed() < 0.25;
    const daysAgo = Math.floor(seed() * 90);

    // Generate a unique fictional email: firstname.lastname@padel24.no
    // On collisions: append a number (anna.berg2@...)
    const emailBase = `${normalizeForEmail(fn)}.${normalizeForEmail(ln)}`;
    const prevCount = emailCounts.get(emailBase) || 0;
    emailCounts.set(emailBase, prevCount + 1);
    const email =
      prevCount === 0 ? `${emailBase}@padel24.no` : `${emailBase}${prevCount + 1}@padel24.no`;

    players.push({
      id: id++,
      name: `${fn} ${ln}`,
      firstName: fn,
      lastName: ln,
      email,
      gender: isF ? 'F' : 'M',
      elo,
      level: Math.min(9, Math.max(1, Math.floor((elo - 500) / 300) + 1)),
      isFavorite,
      lastPlayed: daysAgo,
      tournamentsPlayed: Math.floor(seed() * 20),
      // Newbie-tracking: demo players are assumed established (have played many matches).
      // Real players are migrated via app-load with the actual count from the archive.
      matchesPlayed: 30 + Math.floor(seed() * 100),
      lastMatchAt: Date.now() - daysAgo * 86400 * 1000,
    });
  }
  return players;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
